import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, AlertTriangle, Trash2, FilePlus2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmModal from '../../Common/ConfirmModal';
import { EmptyState } from '../../Common/EmptyState';
import StatusBadge from '../../Common/StatusBadge';
import AssembleFileDialog from './AssembleFileDialog';
import ManualPaymentDialog from './ManualPaymentDialog';
import { apiError, removeFromQueue } from './paymentsApi';
import { SCHEME_LABEL, SOURCE_GROUP, kr } from './paymentStatus';

const GROUP_ORDER = ['payroll', 'supplier_invoices', 'skatteverket', 'manual'];

/**
 * Betalningskö — queued rows grouped by source, selection within one
 * category, and the "Skapa betalfil" entry point.
 */
const PaymentQueueTab = ({ companyId, queue, loading, onRefresh, onFileCreated, initialSelection }) => {
  const [selected, setSelected] = useState(() => new Set(initialSelection || []));
  const [removeTarget, setRemoveTarget] = useState(null);
  const [showAssemble, setShowAssemble] = useState(false);
  const [showManual, setShowManual] = useState(false);

  const items = useMemo(() => queue?.items || [], [queue]);
  const groups = useMemo(() => {
    const by = {};
    for (const item of items) (by[item.source_type] = by[item.source_type] || []).push(item);
    return GROUP_ORDER.filter((k) => by[k]).map((k) => ({ key: k, label: SOURCE_GROUP[k] || k, items: by[k] }));
  }, [items]);

  // The selection follows the queue: a row that left it (removed, put in a
  // file) or turned invalid after a refresh cannot stay selected, where its
  // disabled checkbox could not be cleared and it would count in the total.
  useEffect(() => {
    setSelected((prev) => {
      const selectable = new Set(items.filter((i) => i.valid).map((i) => i.instruction_id));
      const next = new Set([...prev].filter((id) => selectable.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [items]);

  const selectedItems = items.filter((i) => selected.has(i.instruction_id));
  const selectedCategory = selectedItems[0]?.category_purpose || null;
  const selectedTotal = selectedItems.reduce((s, i) => s + Number(i.amount), 0);
  // A stable list while the selection is unchanged: the dialog previews on every change of it.
  const selectedIds = useMemo(() => [...selected], [selected]);

  const refuseMixedFile = () => toast.error('Löner och leverantörsbetalningar kan inte ligga i samma fil.');

  const toggle = (item) => {
    const adding = !selected.has(item.instruction_id);
    if (adding && selectedCategory && item.category_purpose !== selectedCategory) { refuseMixedFile(); return; }
    setSelected((prev) => {
      const next = new Set(prev);
      if (adding) next.add(item.instruction_id); else next.delete(item.instruction_id);
      return next;
    });
  };

  const toggleGroup = (group) => {
    const valid = group.items.filter((i) => i.valid);
    const allIn = valid.every((i) => selected.has(i.instruction_id));
    const category = valid[0]?.category_purpose;
    if (!allIn && category && selectedCategory && selectedCategory !== category) { refuseMixedFile(); return; }
    setSelected((prev) => {
      const next = new Set(prev);
      for (const i of valid) (allIn ? next.delete(i.instruction_id) : next.add(i.instruction_id));
      return next;
    });
  };

  const confirmRemove = async () => {
    const target = removeTarget;
    setRemoveTarget(null);
    try {
      await removeFromQueue(companyId, target.instruction_id, 'Borttagen av användaren');
      setSelected((prev) => { const n = new Set(prev); n.delete(target.instruction_id); return n; });
      toast.success('Betalningen togs bort från kön.');
      onRefresh();
    } catch (err) {
      toast.error(apiError(err, 'Kunde inte ta bort betalningen.').message);
    }
  };

  const manualDialog = (
    <ManualPaymentDialog show={showManual} companyId={companyId} onClose={() => setShowManual(false)} onQueued={() => { setShowManual(false); onRefresh(); }} />
  );

  if (!loading && items.length === 0) {
    return (
      <>
        <EmptyState
          icon={FilePlus2}
          title="Betalningskön är tom"
          description="Lönerna hamnar här när en lönekörning låses, godkända leverantörsfakturor via ”Lägg i betalningskö” och skatt från skattekontot. En enstaka betalning kan läggas till manuellt."
          actions={(
            <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" onClick={() => setShowManual(true)}>
              <Plus size={14} /> Ny manuell betalning
            </button>
          )}
        />
        {manualDialog}
      </>
    );
  }

  return (
    <>
      <div className="modern-card" style={{ marginBottom: 16 }}>
        <div className="modern-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, color: '#475569' }}>
            {selected.size > 0
              ? <>{selected.size} valda · <strong>{kr(selectedTotal)}</strong></>
              : 'Välj betalningar att lägga i en fil. Löner och leverantörsbetalningar skapas som separata filer.'}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" onClick={() => setShowManual(true)}>
              <Plus size={14} /> Ny manuell betalning
            </button>
            <button type="button" className="ll-btn ll-btn--primary ll-btn--sm" disabled={selected.size === 0} onClick={() => setShowAssemble(true)}>
              <FilePlus2 size={14} /> Skapa betalfil
            </button>
          </div>
        </div>
        <div className="modern-card-body" style={{ padding: 0 }}>
          {groups.map((group) => {
            const valid = group.items.filter((i) => i.valid);
            const allIn = valid.length > 0 && valid.every((i) => selected.has(i.instruction_id));
            return (
              <div key={group.key}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', fontSize: 13, fontWeight: 600 }}>
                  <input type="checkbox" checked={allIn} onChange={() => toggleGroup(group)} disabled={valid.length === 0} aria-label={`Välj alla ${group.label}`} />
                  {group.label}
                  <span className="text-muted" style={{ fontWeight: 400 }}>
                    {group.items.length} st · {kr(group.items.reduce((s, i) => s + Number(i.amount), 0))}
                  </span>
                </div>
                <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                  <tbody>
                    {group.items.map((item) => (
                      <tr key={item.instruction_id} style={!item.valid ? { background: '#fef2f2' } : undefined}>
                        <td style={{ width: 32 }}>
                          <input type="checkbox" checked={selected.has(item.instruction_id)} onChange={() => toggle(item)} disabled={!item.valid} aria-label={`Välj ${item.label}`} />
                        </td>
                        <td>{item.label}</td>
                        <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{kr(item.amount)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{item.requested_date}</td>
                        <td style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                          {item.creditor_scheme ? `${SCHEME_LABEL[item.creditor_scheme] || item.creditor_scheme} ${item.creditor_masked}` : '—'}
                          {item.remittance_type === 'SCOR' && item.remittance_ref ? <span className="text-muted" style={{ fontSize: 12 }}> · OCR {item.remittance_ref}</span> : null}
                        </td>
                        <td>
                          {item.errors.length > 0 ? (
                            <div className="text-danger"><AlertCircle size={12} className="me-1" />{item.errors.join(' ')}</div>
                          ) : item.warnings.length > 0 ? (
                            <div style={{ color: '#b45309' }}><AlertTriangle size={12} className="me-1" />{item.warnings.join(' ')}</div>
                          ) : (
                            <StatusBadge tone="success">Klar</StatusBadge>
                          )}
                        </td>
                        <td style={{ width: 40, textAlign: 'right' }}>
                          <button type="button" className="ll-btn ll-btn--ghost ll-btn--sm" title="Ta bort från kön" onClick={() => setRemoveTarget(item)}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </div>

      <ConfirmModal
        show={Boolean(removeTarget)}
        title="Ta bort betalningen från kön?"
        body="Betalningen försvinner från betalningskön. Löner kan läggas tillbaka från lönekörningen."
        detail={removeTarget ? `${removeTarget.label} · ${kr(removeTarget.amount)}` : null}
        confirmLabel="Ta bort"
        onConfirm={confirmRemove}
        onCancel={() => setRemoveTarget(null)}
      />

      {manualDialog}

      <AssembleFileDialog
        show={showAssemble}
        companyId={companyId}
        instructionIds={selectedIds}
        onClose={() => setShowAssemble(false)}
        onCreated={(file) => { setShowAssemble(false); setSelected(new Set()); onFileCreated(file); }}
      />
    </>
  );
};

export default PaymentQueueTab;
